import React, { useEffect, useRef } from 'react';

/**
 * Ambient animated background - multiple rotating logo clusters
 * - New cluster spawns every 3 seconds
 * - Clusters fade out after ~10 seconds
 * - Max ~10 clusters visible, staggered add/remove
 * - Each cluster has slightly different color variation
 */
const SpawnBackground = () => {
  const canvasRef = useRef(null);
  const animationRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    let width = canvas.offsetWidth || window.innerWidth;
    let height = canvas.offsetHeight || window.innerHeight;

    if (width === 0) width = window.innerWidth;
    if (height === 0) height = window.innerHeight;

    const dpr = window.devicePixelRatio || 1;
    canvas.width = width * dpr;
    canvas.height = height * dpr;
    ctx.scale(dpr, dpr);

    // Cluster class
    class LogoCluster {
      constructor(x, y) {
        this.x = x;
        this.y = y;
        this.rotation = Math.random() * Math.PI * 2;
        this.rotationSpeed = 0.001 + Math.random() * 0.002;
        this.scale = Math.min(width, height) * (0.08 + Math.random() * 0.06);
        this.branchExpansion = 0;
        this.opacity = 0; // Fade in
        this.fadeState = 'in'; // 'in', 'visible', 'out'
        this.createdAt = Date.now();

        // Color variation (purple/indigo range)
        this.hue = 245 + Math.random() * 30; // 245-275
        this.saturation = 60 + Math.random() * 20;
        this.lightness = 55 + Math.random() * 15;

        // Branch angles (5 branches like the logo)
        this.branches = [
          { angle: -Math.PI / 2, length: 0.7 },
          { angle: -Math.PI / 5, length: 0.65 },
          { angle: Math.PI / 6, length: 0.7 },
          { angle: Math.PI / 2.5, length: 0.6 },
          { angle: Math.PI + Math.PI / 8, length: 0.7 }
        ];
      }

      update() {
        // Rotation
        this.rotation += this.rotationSpeed;

        // Branch expansion
        if (this.branchExpansion < 1) {
          this.branchExpansion = Math.min(1, this.branchExpansion + 0.008);
        }

        // Fade states
        if (this.fadeState === 'in') {
          this.opacity = Math.min(1, this.opacity + 0.02);
          if (this.opacity >= 1) this.fadeState = 'visible';
        } else if (this.fadeState === 'out') {
          this.opacity = Math.max(0, this.opacity - 0.015);
        }
      }

      startFadeOut() {
        this.fadeState = 'out';
      }

      isDead() {
        return this.fadeState === 'out' && this.opacity <= 0;
      }

      draw(ctx) {
        if (this.opacity <= 0) return;

        const scale = this.scale;
        const opacity = this.opacity;

        ctx.save();
        ctx.translate(this.x, this.y);
        ctx.rotate(this.rotation);

        // Colors with opacity
        const baseColor = `hsla(${this.hue}, ${this.saturation}%, ${this.lightness}%, ${opacity})`;
        const glowColor = `hsla(${this.hue}, ${this.saturation}%, ${this.lightness - 10}%, ${opacity * 0.15})`;
        const lineColor = `hsla(${this.hue}, ${this.saturation}%, ${this.lightness - 5}%, ${opacity * 0.6})`;

        // Draw glow behind everything
        const glowGradient = ctx.createRadialGradient(0, 0, 0, 0, 0, scale * 0.9);
        glowGradient.addColorStop(0, glowColor);
        glowGradient.addColorStop(0.5, `hsla(${this.hue}, ${this.saturation}%, ${this.lightness - 10}%, ${opacity * 0.05})`);
        glowGradient.addColorStop(1, 'transparent');
        ctx.fillStyle = glowGradient;
        ctx.beginPath();
        ctx.arc(0, 0, scale * 0.9, 0, Math.PI * 2);
        ctx.fill();

        // Draw branches
        if (this.branchExpansion > 0) {
          ctx.strokeStyle = lineColor;
          ctx.lineWidth = Math.max(1.5, scale * 0.025);
          ctx.lineCap = 'round';

          this.branches.forEach((branch, i) => {
            const branchProgress = Math.max(0, (this.branchExpansion - i * 0.1) / 0.5);
            const progress = Math.min(1, branchProgress);

            if (progress > 0) {
              const endX = Math.cos(branch.angle) * scale * branch.length * progress;
              const endY = Math.sin(branch.angle) * scale * branch.length * progress;

              // Draw line
              ctx.beginPath();
              ctx.moveTo(0, 0);
              ctx.lineTo(endX, endY);
              ctx.stroke();

              // Draw end dot
              if (progress > 0.5) {
                const dotProgress = (progress - 0.5) * 2;
                const dotRadius = scale * 0.07 * dotProgress;

                // Dot glow
                const dotGlow = ctx.createRadialGradient(endX, endY, 0, endX, endY, dotRadius * 2);
                dotGlow.addColorStop(0, `hsla(${this.hue}, ${this.saturation}%, ${this.lightness}%, ${0.3 * dotProgress * opacity})`);
                dotGlow.addColorStop(1, 'transparent');
                ctx.fillStyle = dotGlow;
                ctx.beginPath();
                ctx.arc(endX, endY, dotRadius * 2, 0, Math.PI * 2);
                ctx.fill();

                // Dot
                ctx.fillStyle = `hsla(${this.hue}, ${this.saturation}%, ${this.lightness + 10}%, ${0.9 * dotProgress * opacity})`;
                ctx.beginPath();
                ctx.arc(endX, endY, dotRadius, 0, Math.PI * 2);
                ctx.fill();
              }
            }
          });
        }

        // Draw center dot
        const centerRadius = scale * 0.12;

        // Center glow
        const centerGlow = ctx.createRadialGradient(0, 0, 0, 0, 0, centerRadius * 2.5);
        centerGlow.addColorStop(0, `hsla(${this.hue}, ${this.saturation}%, ${this.lightness}%, ${0.4 * opacity})`);
        centerGlow.addColorStop(0.5, `hsla(${this.hue}, ${this.saturation}%, ${this.lightness - 5}%, ${0.15 * opacity})`);
        centerGlow.addColorStop(1, 'transparent');
        ctx.fillStyle = centerGlow;
        ctx.beginPath();
        ctx.arc(0, 0, centerRadius * 2.5, 0, Math.PI * 2);
        ctx.fill();

        // Center dot
        ctx.fillStyle = baseColor;
        ctx.beginPath();
        ctx.arc(0, 0, centerRadius, 0, Math.PI * 2);
        ctx.fill();

        ctx.restore();
      }
    }

    // Cluster management
    const clusters = [];
    let lastSpawnTime = 0;
    const SPAWN_INTERVAL = 3000; // New cluster every 3 seconds
    const FADE_AFTER = 10000; // Start fading after 10 seconds
    const MAX_CLUSTERS = 10;

    // Spawn a cluster at random position (biased toward right side)
    const spawnCluster = () => {
      const x = width * 0.3 + Math.random() * width * 0.6; // 30-90% of width
      const y = height * 0.15 + Math.random() * height * 0.7; // 15-85% of height
      return new LogoCluster(x, y);
    };

    // Start with a few clusters
    for (let i = 0; i < 3; i++) {
      const cluster = spawnCluster();
      cluster.opacity = 1;
      cluster.fadeState = 'visible';
      cluster.branchExpansion = 1;
      cluster.createdAt = Date.now() - (i * 3000); // Stagger ages
      clusters.push(cluster);
    }

    // Animation loop
    const animate = () => {
      const now = Date.now();

      // Clear with dark gradient background
      const bgGradient = ctx.createLinearGradient(0, 0, width, 0);
      bgGradient.addColorStop(0, '#0a0a12');
      bgGradient.addColorStop(0.5, '#0c0c18');
      bgGradient.addColorStop(1, '#080810');
      ctx.fillStyle = bgGradient;
      ctx.fillRect(0, 0, width, height);

      // Spawn new cluster
      if (now - lastSpawnTime > SPAWN_INTERVAL) {
        if (clusters.length < MAX_CLUSTERS) {
          clusters.push(spawnCluster());
        } else {
          // Find oldest visible cluster to fade out, then add new one
          const oldestVisible = clusters.find(c => c.fadeState === 'visible');
          if (oldestVisible) {
            oldestVisible.startFadeOut();
          }
          clusters.push(spawnCluster());
        }
        lastSpawnTime = now;
      }

      // Check for clusters that should start fading (based on age)
      clusters.forEach(cluster => {
        if (cluster.fadeState === 'visible' && now - cluster.createdAt > FADE_AFTER) {
          // Only start fading if we have enough clusters
          if (clusters.filter(c => c.fadeState !== 'out').length > 5) {
            cluster.startFadeOut();
          }
        }
      });

      // Update and draw all clusters
      for (let i = clusters.length - 1; i >= 0; i--) {
        clusters[i].update();
        clusters[i].draw(ctx);

        // Remove dead clusters
        if (clusters[i].isDead()) {
          clusters.splice(i, 1);
        }
      }

      animationRef.current = requestAnimationFrame(animate);
    };

    animate();

    // Handle resize
    const handleResize = () => {
      width = canvas.offsetWidth || window.innerWidth;
      height = canvas.offsetHeight || window.innerHeight;
      if (width === 0) width = window.innerWidth;
      if (height === 0) height = window.innerHeight;
      canvas.width = width * dpr;
      canvas.height = height * dpr;
      ctx.scale(dpr, dpr);
    };

    window.addEventListener('resize', handleResize);

    return () => {
      cancelAnimationFrame(animationRef.current);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        zIndex: 0
      }}
    />
  );
};

export default SpawnBackground;
